import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete using OpenStreetMap Nominatim.
 * No API key required. Rate limited to ~1 req/s per app.
 *
 * onSelect receives:
 *   { display: full text, direccion, comuna, region, lat, lon }
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, testId }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = async (q) => {
    if (!q || q.length < 4) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      // Si hay un número al final de la query (ej "Mar Jonico 7995"),
      // hacemos dos búsquedas en paralelo: la query completa Y la query sin
      // el número, para mejorar la tasa de aciertos en Nominatim (Chile).
      const trimmed = q.trim();
      const numMatch = trimmed.match(/^(.*?)\s+(\d+[a-zA-Z]?)\s*$/);
      const queries = [trimmed];
      if (numMatch) queries.push(numMatch[1].trim());

      const fetchOne = async (query) => {
        const url = new URL("https://nominatim.openstreetmap.org/search");
        url.searchParams.set("q", `${query}, Chile`);
        url.searchParams.set("countrycodes", "cl");
        url.searchParams.set("format", "jsonv2");
        url.searchParams.set("addressdetails", "1");
        url.searchParams.set("limit", "6");
        url.searchParams.set("accept-language", "es");
        const res = await fetch(url.toString(), { headers: { "Accept": "application/json" } });
        return res.ok ? res.json() : [];
      };

      const allResults = await Promise.all(queries.map(fetchOne));
      const flat = allResults.flat();
      const seen = new Set();
      const userNum = numMatch ? numMatch[2] : null;

      const items = flat
        .map((r) => {
          const a = r.address || {};
          // Si el usuario incluyó número en la query pero Nominatim no lo
          // detectó, lo "inyectamos" en el display para que se vea correcto.
          const houseNum = a.house_number || userNum || "";
          const street = [a.road, houseNum].filter(Boolean).join(" ");
          const comuna = a.city_district || a.suburb || a.town || a.city || a.municipality || a.village || "";
          const region = a.state || a.region || "";
          let display = r.display_name;
          if (userNum && !a.house_number && street) {
            // Reemplazar solo el primer segmento del display por la calle con número
            const rest = (r.display_name || "").split(",").slice(1).join(",").trim();
            display = rest ? `${street}, ${rest}` : street;
          }
          return {
            display,
            short: street || display.split(",")[0],
            direccion: street || display.split(",")[0],
            comuna,
            region,
            lat: r.lat,
            lon: r.lon,
            raw: r,
          };
        })
        .filter((it) => {
          const key = `${it.short}|${it.comuna}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 6);

      setResults(items);
      setOpen(true);
      setActiveIdx(-1);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onInput = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 350);
  };

  const select = (item) => {
    onChange(item.display);
    onSelect?.(item);
    setOpen(false);
    setResults([]);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      select(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-soft)" }}/>
        <input
          type="text"
          value={value}
          onChange={onInput}
          onKeyDown={onKey}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder || "Empieza a escribir tu dirección…"}
          className="field-input field-input--compact pl-9"
          autoComplete="off"
          data-testid={testId}
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin" style={{ color: "var(--text-soft)" }}/>
        )}
      </div>
      {open && (results.length > 0 || (value && value.length >= 4)) && (
        <div className="absolute left-0 right-0 mt-1 z-30 rounded-lg border bg-white shadow-lg overflow-hidden max-h-80 overflow-y-auto" style={{ borderColor: "var(--border-medium)" }}>
          {results.map((r, i) => (
            <button
              key={r.lat + "," + r.lon + i}
              type="button"
              onClick={() => select(r)}
              onMouseEnter={() => setActiveIdx(i)}
              className="w-full text-left px-3 py-2.5 transition border-b last:border-b-0"
              style={{
                background: activeIdx === i ? "var(--bg-soft)" : "white",
                borderColor: "var(--border-subtle)",
              }}
              data-testid={`${testId}-result-${i}`}
            >
              <div className="text-[13px] font-semibold leading-tight" style={{ color: "var(--text-primary)" }}>
                {r.short || r.display.split(",")[0]}
              </div>
              <div className="text-[11.5px] mt-0.5 leading-tight" style={{ color: "var(--text-muted)" }}>
                {r.display.split(",").slice(1).join(",").trim()}
              </div>
            </button>
          ))}
          {value && value.trim().length >= 4 && (
            <button
              type="button"
              onClick={() => select({ display: value.trim(), short: value.trim(), direccion: value.trim(), comuna: "", region: "", lat: "", lon: "" })}
              className="w-full text-left px-3 py-2.5 transition flex items-start gap-2"
              style={{ background: "var(--bg-soft)", borderTop: results.length > 0 ? "1px solid var(--border-subtle)" : "none" }}
              data-testid={`${testId}-manual`}
            >
              <span className="text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded mt-0.5" style={{ background: "var(--brand-accent)", color: "white" }}>Manual</span>
              <span className="flex-1">
                <span className="text-[12.5px] font-semibold leading-tight block" style={{ color: "var(--text-primary)" }}>
                  Usar "{value.trim()}"
                </span>
                <span className="text-[11px] mt-0.5 leading-tight block" style={{ color: "var(--text-muted)" }}>
                  ¿No aparece tu dirección? Continúa con el texto que escribiste.
                </span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
